import { Accordion, Base, Caption, Lead, Link, Prose, Spinner } from '@sbab-ui/components'
import { forwardRef, useEffect, useState } from 'react'
import { TotalCostData, fetchTotalCost } from '../../api/httpHandlers'
import AccordionTriggerWithClick from './AccordionTriggerWithClick'
import TableRow from './TableRow'
import { UserValues } from '../../appState'
import { track } from '../../lib/tracking'
import { name as appName } from '../../../package.json'

interface Props {
  userValues: UserValues
  threeMonthInterestRate: number
  priceMatrixDifference: number
  effectiveInterest: number
  blur: boolean
}

const EffectiveRateAccordionSection = forwardRef(
  (
    {
      userValues,
      threeMonthInterestRate,
      priceMatrixDifference,
      effectiveInterest,
      blur,
    }: Readonly<Props>,
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const [totalCostData, setTotalCostData] = useState<null | TotalCostData>(null)

    // Calculate interest rate
    const { wantedLoanAmount } = userValues

    // Add price difference to interest rate
    const interestRate = threeMonthInterestRate + priceMatrixDifference

    // Values for table
    useEffect(() => {
      async function fetchData() {
        const response = await fetchTotalCost(interestRate, wantedLoanAmount)
        if (response.successful && response.data) {
          setTotalCostData(response.data)
        } else {
          throw new Error('Could not fetch total cost')
        }
      }
      fetchData().catch(() => {
        throw new Error('Could not fetch total cost')
      })
    }, [interestRate, wantedLoanAmount])

    const straightLineAmortizationPerMonth = wantedLoanAmount / 480
    const interestCostPerMonth = (wantedLoanAmount * (interestRate / 100)) / 12
    const sumToPayPerMonth = Math.ceil(straightLineAmortizationPerMonth + interestCostPerMonth)

    return (
      <Accordion.Root>
        <Accordion.Item className="border-neutral-85" ref={ref}>
          <AccordionTriggerWithClick
            handleOnClick={() => track('click', 'accordion-open-close.effective-rate-accordion')}
          >
            Läs mer om effektiv ränta och se räkneexempel
          </AccordionTriggerWithClick>
          <Accordion.Content>
            <div
              className={`flex flex-col gap-8 ${blur ? 'text-neutral-95 opacity-50' : 'text-black'}`}
            >
              <Base className="!mt-0">
                Ränteavdraget för Kilowattlånet är inte medräknat i räkneexemplet nedan
              </Base>
              <div className="flex flex-col gap-4">
                {totalCostData ? (
                  <div>
                    <hr className="h-px border-t-0 bg-neutral-85" />
                    <TableRow value={`${wantedLoanAmount.toLocaleString('sv-SE')} kr`} blur={blur}>
                      <Caption className="!mt-0">Bolån*</Caption>
                    </TableRow>
                    <TableRow
                      value={`${interestRate.toFixed(2).replace('.', ',')} %**`}
                      blur={blur}
                    >
                      <Caption className="!mt-0">Din ränta SBAB bolån 3 mån</Caption>
                    </TableRow>
                    <TableRow
                      value={`${effectiveInterest.toFixed(2).replace('.', ',')} %`}
                      blur={blur}
                    >
                      <Caption className="!mt-0">Effektiv ränta</Caption>
                    </TableRow>
                    <TableRow
                      value={`${sumToPayPerMonth.toLocaleString('sv-SE')} kr/mån`}
                      blur={blur}
                    >
                      <Caption className="!mt-0">Summa att betala per månad***</Caption>
                    </TableRow>
                    <TableRow
                      value={`${totalCostData.totalPayment.toLocaleString('sv-SE')} kr`}
                      blur={blur}
                    >
                      <Caption className="!mt-0">
                        Sammanlagt belopp du betalar under lånets löptid****
                      </Caption>
                    </TableRow>
                  </div>
                ) : (
                  <div className="flex h-96 items-center justify-center">
                    <Spinner size="large" data-testid="spinner" />
                  </div>
                )}
                <div className="text-neutral-40">
                  <Caption className="!mt-0">
                    * Bolånet förutsätter panträtt i fast egendom, tomträtt, bostadsrätt eller andel
                    i bostadsföreningen.
                  </Caption>
                  <Caption className="!mt-0">
                    ** Förutsatt att lånet betalas med autogiro eller e-faktura. SBAB tar inte ut
                    någon uppläggningsavgift.
                  </Caption>
                  <Caption className="!mt-0">
                    *** Amortering och räntekostnad per månad. Beräknat på rak amortering med 40 års
                    löptid och utan skatteavdrag för ränteutgifter.
                  </Caption>
                  <Caption className="!mt-0">
                    **** Totalt belopp som har betalats (amorteringar och räntekostnad) efter 480
                    månadsbetalningar. Beräkningen förutsätter att räntan är oförändrad under hela
                    lånets löptid.
                  </Caption>
                </div>
              </div>
              <Prose>
                <Lead>Vad är effektiv ränta?</Lead>
                <Base>
                  Effektiv ränta är den totala kostnaden du betalar för ditt bolån varje år. I den
                  ingår både boräntan och andra eventuella avgifter för exempelvis pappersavier
                  eller administration. Den effektiva räntan påverkas också av hur ofta du gör
                  inbetalningar eftersom det blir en så kallad ”ränta på ränta”-effekt.
                </Base>
                <Base>
                  Den effektiva räntan anges som en årsränta på hela lånebeloppet. Genom att jämföra
                  den effektiva räntan för olika bolån får du en bild av vad totalkostnaden blir hos
                  olika långivare.
                </Base>
              </Prose>
              <Base>
                Har du frågor?{' '}
                <Link
                  href={`/1/privat/kundservice/kontakt/kontakta_oss.html?ref=${appName}`}
                  className="!text-semantic-link"
                  external
                >
                  Kontakta vår kundservice
                </Link>
              </Base>
            </div>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion.Root>
    )
  },
)

EffectiveRateAccordionSection.displayName = 'EffectiveRateAccordionSection'
export default EffectiveRateAccordionSection
